import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Bot, Trash2, Pencil, ExternalLink, Copy, Loader2, CheckCircle2 } from "lucide-react";

/**
 * Assistants — focused view on the VAPI-side settings of an agent.
 * Every "assistant" here is really an agent with channels ⊇ [voice_calling]
 * and a provisioned `vapi_assistant_id`. Creating from this page auto-fills
 * the calling channel and provisions the VAPI assistant immediately so it's
 * ready to dial without visiting the full agent builder.
 */
export default function AssistantsPage() {
  const [assistants, setAssistants] = useState([]);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [editing, setEditing] = useState(null); // holds the assistant being edited, or null

  const emptyForm = {
    name: "",
    welcome_message: "Hello, thanks for taking my call. How may I help you today?",
    system_prompt: "You are a friendly, professional AI assistant. Keep responses under two sentences. Ask one question at a time.",
    voice_id: "alloy",
    model: "gpt-4o-mini",
    temperature: 0.6,
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const [ag, vs] = await Promise.all([api.get("/agents"), api.get("/calls/voices").catch(() => ({ data: { voices: [] } }))]);
      // Assistants page shows ONLY calling-focused records so it doesn't
      // overlap with the Agents page (Web Chat / Web Voice).
      const calling = (ag.data || []).filter((a) => a.type === "voice_calling");
      setAssistants(calling);
      setVoices(vs.data.voices || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || "",
      welcome_message: a.welcome_message || "",
      system_prompt: a.system_prompt || "",
      voice_id: a.voice_id || "alloy",
      model: a.model || "gpt-4o-mini",
      temperature: a.temperature ?? 0.6,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Give the assistant a name");
    if (!form.system_prompt.trim()) return toast.error("System prompt is required");
    setBusy(true);
    try {
      if (editing) {
        // EDIT — PUT the agent doc, then re-sync to VAPI so the assistant on
        // VAPI's side reflects the new prompt/voice/model right away.
        const payload = {
          ...editing,
          name: form.name.trim(),
          welcome_message: form.welcome_message.trim() || "Hello!",
          system_prompt: form.system_prompt.trim(),
          voice_id: form.voice_id || "alloy",
          model: form.model || "gpt-4o-mini",
          temperature: Number(form.temperature) || 0.6,
        };
        delete payload.id; delete payload.tenant_id; delete payload.created_at;
        await api.put(`/agents/${editing.id}`, payload);
        // If it never had a VAPI assistant, provision now.
        if (!editing.vapi_assistant_id) {
          try { await api.post("/calls/vapi/assistant", { agent_id: editing.id }); } catch { /* handled by toast below */ }
        }
        toast.success("Assistant updated — changes synced to the calling server.");
      } else {
        // CREATE
        const { data: agent } = await api.post("/agents", {
          name: form.name.trim(),
          type: "voice_calling",
          channels: ["voice_calling"],
          system_prompt: form.system_prompt.trim(),
          welcome_message: form.welcome_message.trim() || "Hello!",
          voice_id: form.voice_id || "alloy",
          model: form.model || "gpt-4o-mini",
          temperature: Number(form.temperature) || 0.6,
          language: "English",
          status: "active",
        });
        try {
          await api.post("/calls/vapi/assistant", { agent_id: agent.id });
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Assistant created in HelloSir, but Calling-server registration failed. Open it to retry.");
        }
        toast.success("Assistant created — ready to dial from Outbound calls.");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editing ? "Failed to update assistant" : "Failed to create assistant"));
    } finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? This also removes it from the calling server on next sync.`)) return;
    try {
      await api.delete(`/agents/${a.id}`);
      toast.success("Deleted"); load();
    } catch { toast.error("Delete failed"); }
  };

  const copyVapiId = (a) => {
    if (!a.vapi_assistant_id) return;
    navigator.clipboard?.writeText(a.vapi_assistant_id);
    setCopiedId(a.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div className="space-y-6" data-testid="assistants-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-extrabold text-3xl">Assistants</h1>
          <p className="text-sm text-[#4B5563]">Voice-calling assistants. Each one is provisioned on the calling server and can be attached to any outbound campaign.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild>
            <button className="btn-primary flex items-center gap-2" data-testid="new-assistant-button" onClick={openCreate}>
              <Plus className="w-4 h-4" /> Create Assistant
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle>{editing ? `Edit assistant — ${editing.name}` : "New assistant"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-mono uppercase text-[#4B5563]">Name</label>
                <input
                  data-testid="assistant-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Riley — Wellness scheduler"
                  className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm text-sm mt-1"
                />
              </div>

              <div>
                <label className="text-[10px] font-mono uppercase text-[#4B5563]">First message</label>
                <input
                  data-testid="assistant-first-message-input"
                  value={form.welcome_message}
                  onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
                  className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm text-sm mt-1"
                />
                <div className="text-[10px] text-[#4B5563] mt-0.5">What the assistant says the moment the call connects.</div>
              </div>

              <div>
                <label className="text-[10px] font-mono uppercase text-[#4B5563]">System prompt</label>
                <textarea
                  data-testid="assistant-system-prompt-input"
                  value={form.system_prompt}
                  onChange={(e) => setForm({ ...form, system_prompt: e.target.value })}
                  rows={7}
                  className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm text-xs font-mono mt-1"
                />
                <div className="text-[10px] text-[#4B5563] mt-0.5">Tone, goal, and rules. Use &quot;##&quot; section headings for structure like the calling-server examples.</div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] font-mono uppercase text-[#4B5563]">Voice</label>
                  <select
                    data-testid="assistant-voice-select"
                    value={form.voice_id}
                    onChange={(e) => setForm({ ...form, voice_id: e.target.value })}
                    className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm bg-white text-sm mt-1"
                  >
                    {(voices.length ? voices : [{ id: "alloy", name: "Alloy" }]).map((v) => (
                      <option key={v.id} value={v.id}>{v.name || v.id}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase text-[#4B5563]">Model</label>
                  <select
                    data-testid="assistant-model-select"
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                    className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm bg-white text-sm mt-1"
                  >
                    <option value="gpt-4o-mini">GPT-4o mini (fast)</option>
                    <option value="gpt-4o">GPT-4o (smart)</option>
                    <option value="gpt-4.1">GPT-4.1</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase text-[#4B5563]">Temperature</label>
                  <input
                    data-testid="assistant-temperature-input"
                    type="number" min={0} max={2} step={0.1}
                    value={form.temperature}
                    onChange={(e) => setForm({ ...form, temperature: e.target.value })}
                    className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm text-sm mt-1 font-mono"
                  />
                </div>
              </div>

              <button
                data-testid="assistant-save-submit"
                disabled={busy}
                onClick={save}
                className="btn-primary w-full disabled:opacity-60"
              >
                {busy
                  ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {editing ? "Saving & syncing to calling server…" : "Creating & provisioning on calling server…"}</span>
                  : (editing ? "Save changes" : "Create assistant")}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="hs-card p-12 text-center text-sm text-[#4B5563]">Loading assistants…</div>
      ) : assistants.length === 0 ? (
        <div className="hs-card p-12 text-center" data-testid="assistants-empty">
          <Bot className="w-10 h-10 mx-auto text-[#4B5563]" />
          <div className="font-display font-bold text-lg mt-4">No assistants yet</div>
          <div className="text-sm text-[#4B5563]">Create one and it&apos;ll be provisioned on the calling server automatically. You can then attach it to any outbound campaign.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assistants.map((a) => (
            <div key={a.id} className="hs-card p-6 flex flex-col" data-testid={`assistant-card-${a.id}`}>
              <div className="flex items-start justify-between">
                <Bot className="w-7 h-7 text-[#F97316]" />
                {a.vapi_assistant_id ? (
                  <span className="text-[10px] font-mono px-2 py-0.5 bg-[#10B981]/10 text-[#10B981] inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> live · calling server
                  </span>
                ) : (
                  <span className="text-[10px] font-mono px-2 py-0.5 bg-[#F59E0B]/10 text-[#F59E0B]">not provisioned</span>
                )}
              </div>
              <div className="font-display font-bold text-lg mt-4 truncate">{a.name}</div>
              <div className="text-xs font-mono text-[#4B5563] mt-1">{a.voice_id} · {a.model || "gpt-4o-mini"} · {a.language || "English"}</div>
              <p className="text-xs text-[#4B5563] mt-3 line-clamp-3">{a.system_prompt}</p>

              {a.vapi_assistant_id && (
                <button
                  onClick={() => copyVapiId(a)}
                  className="mt-3 text-[10px] font-mono text-[#4B5563] hover:text-[#0F1115] inline-flex items-center gap-1.5 self-start"
                  data-testid={`assistant-copy-vapi-id-${a.id}`}
                >
                  <Copy className="w-3 h-3" />
                  {copiedId === a.id ? "copied" : `calling-server id: ${a.vapi_assistant_id.slice(0, 8)}…`}
                </button>
              )}

              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#E2E8F0]">
                <button onClick={() => openEdit(a)} className="btn-primary text-xs flex-1 text-center flex items-center justify-center gap-1" data-testid={`assistant-edit-${a.id}`}>
                  <Pencil className="w-3 h-3" /> Edit
                </button>
                <Link to="/dashboard/calls/outbound" className="btn-outline text-xs px-3" title="Use in campaign" data-testid={`assistant-use-in-campaign-${a.id}`}>
                  <ExternalLink className="w-4 h-4" />
                </Link>
                <button onClick={() => remove(a)} className="btn-outline text-xs px-3" data-testid={`assistant-delete-${a.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
